import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { BaseDbObject } from "../models/db";
import {
  fetchApiDelete,
  fetchApiGet,
  fetchApiPost,
  fetchApiPut,
} from "./utils";
import { Commodities } from "./commodity";

// Type definitions
export type RowType = "option" | "future" | null;
export type FilterType = "whitelist" | "blacklist";

// Option month regex: one letter followed by one digit
const optionMonthRegex = /^[A-Z]\d$/;

// Schema for the dorman adjusted_margin_report_option_month_filters setting value
const AdjustedMarginReportOptionMonthFiltersSchema = z.object({
  filter_type: z.enum(['whitelist', 'blacklist']),
  commodity_type: z.nativeEnum(Commodities).nullable(),
  row_type: z.enum(['option', 'future']).nullable(),
  option_months: z.array(z.string().regex(optionMonthRegex, 'Option month must be one letter followed by one digit (e.g., H6, Z7)'))
});

// Map of provider_key + setting_key to their respective schemas
const settingSchemas = {
  'dorman:adjusted_margin_report_option_month_filters': AdjustedMarginReportOptionMonthFiltersSchema
} as const;

// Validate setting_value based on provider_key and setting_key
export function validateSettingValue(provider_key: string, setting_key: string, setting_value: unknown) {
  const schemaKey = `${provider_key}:${setting_key}` as keyof typeof settingSchemas;
  const schema = settingSchemas[schemaKey];
  
  if (!schema) {
    throw new Error(`Unknown provider_key/setting_key combination: ${schemaKey}`);
  }
  
  return schema.parse(setting_value);
}

// Get the schema for a specific provider_key and setting_key
export function getSettingSchema(provider_key: string, setting_key: string) {
  const schemaKey = `${provider_key}:${setting_key}` as keyof typeof settingSchemas;
  return settingSchemas[schemaKey];
}

// Type inference from schemas
export type AdjustedMarginReportOptionMonthFilters = z.infer<typeof AdjustedMarginReportOptionMonthFiltersSchema>;

export const CreateReportSettingValidation = z.object({
  provider_key: z.string(),
  setting_key: z.string(),
  setting_value: z.any(),
});

export const UpdateReportSettingValidation = z.object({
  id: z.string().uuid(),
  provider_key: z.string(),
  setting_key: z.string(),
  setting_value: z.any(),
});

export const DeleteReportSettingValidation = z.object({
  id: z.string().uuid(),
});

// Type inference from validations
export type CreateReportSettingParams = z.infer<typeof CreateReportSettingValidation>;
export type UpdateReportSettingParams = z.infer<typeof UpdateReportSettingValidation>;

// Entity interface
export interface ReportSetting extends BaseDbObject {
  provider_key: string;
  setting_key: string;
  setting_value: any;
}

// Hooks
export function useListReportSettings() {
  const queryParams = new URLSearchParams();

  return useQuery({
    queryKey: ["report-settings", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<ReportSetting[]>("report-settings", queryParams),
    retry: 1,
  });
}

export function useGetReportSetting(id?: string | null) {
  return useQuery({
    queryKey: ["report-settings", id],
    queryFn: () => fetchApiGet<ReportSetting>(`report-settings/${id}`),
    retry: 1,
    enabled: !!id,
  });
}

export function useCreateReportSetting() {
  const queryClient = useQueryClient();

  return async (params: CreateReportSettingParams) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<ReportSetting>(
      `report-settings`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["report-settings"] });
    }

    return result;
  };
}

export function useUpdateReportSetting() {
  const queryClient = useQueryClient();

  return async (params: UpdateReportSettingParams) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPut<ReportSetting>(
      `report-settings/${params.id}`,
      params,
      queryParams
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["report-settings"] });
    }

    return result;
  };
}

export function useDeleteReportSetting() {
  const queryClient = useQueryClient();

  return async (params: z.infer<typeof DeleteReportSettingValidation>) => {
    const result = await fetchApiDelete<ReportSetting>(
      `report-settings/${params.id}`
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["report-settings"] });
    }

    return result;
  };
}

// Dorman Margin Call Manual Send
export const SendDormanMarginCallValidation = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format'),
});

export type SendDormanMarginCallParams = z.infer<typeof SendDormanMarginCallValidation>;

export function useSendDormanMarginCall() {
  return async (params: SendDormanMarginCallParams) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<{}>(
      `brokerage/reports/dorman/margin-call`,
      params,
      queryParams
    );

    return result;
  };
}
